import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Users, Plus, Search, Filter, Phone, Mail, Calendar, TrendingUp,
  ChevronRight, Clock, Star, UserPlus, Building2, MapPin
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SOURCE_OPTIONS = [
  { value: 'website', label: 'Website' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'phone_inquiry', label: 'Phone Inquiry' },
  { value: 'referral', label: 'Referral' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'property_portal', label: 'Property Portal' },
  { value: 'newspaper', label: 'Newspaper' },
  { value: 'other', label: 'Other' },
];

const INTEREST_OPTIONS = [
  { value: 'plotting', label: 'Plotting' },
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'multiple', label: 'Multiple' },
];

const STATUS_CONFIG = {
  new: { label: 'New', color: 'blue' },
  contacted: { label: 'Contacted', color: 'purple' },
  qualified: { label: 'Qualified', color: 'emerald' },
  unqualified: { label: 'Unqualified', color: 'stone' },
  site_visit_scheduled: { label: 'Site Visit Scheduled', color: 'amber' },
  site_visited: { label: 'Site Visited', color: 'teal' },
  negotiation: { label: 'Negotiation', color: 'orange' },
  converted: { label: 'Converted', color: 'green' },
  lost: { label: 'Lost', color: 'red' },
};

const PRIORITY_CONFIG = {
  hot: { label: 'Hot', color: 'red', icon: '🔥' },
  warm: { label: 'Warm', color: 'amber', icon: '☀️' },
  cold: { label: 'Cold', color: 'blue', icon: '❄️' },
};

const LeadsPage = () => {
  const { user } = useOutletContext();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [newLead, setNewLead] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    source: 'website',
    interestType: '',
    budget: '',
    preferredLocation: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, [statusFilter, priorityFilter]);

  const fetchLeads = async () => {
    const token = localStorage.getItem('crm_token');
    try {
      let url = `${API}/leads/?limit=100`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (priorityFilter !== 'all') url += `&priority=${priorityFilter}`;
      if (searchQuery) url += `&search=${searchQuery}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('crm_token');
    try {
      const response = await axios.get(`${API}/leads/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
    }
  };

  const createLead = async () => {
    if (!newLead.firstName || !newLead.lastName || !newLead.phone) {
      toast.error('Please fill required fields');
      return;
    }

    const token = localStorage.getItem('crm_token');
    setSubmitting(true);

    try {
      // Clean up empty strings to null for optional fields
      const leadData = {
        ...newLead,
        email: newLead.email || null,
        interestType: newLead.interestType || null,
        budget: newLead.budget || null,
        preferredLocation: newLead.preferredLocation || null,
        notes: newLead.notes || null
      };
      await axios.post(`${API}/leads/`, leadData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Lead created successfully');
      setCreateDialogOpen(false);
      setNewLead({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        source: 'website',
        interestType: '',
        budget: '',
        preferredLocation: '',
        notes: ''
      });
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error('Failed to create lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      fetchLeads();
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-900">Leads</h1>
          <p className="text-stone-500 mt-1">Manage and track your sales leads</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="mt-4 md:mt-0 bg-emerald-900 hover:bg-emerald-800" data-testid="add-lead-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">First Name *</label>
                  <Input
                    placeholder="First name"
                    value={newLead.firstName}
                    onChange={(e) => setNewLead({ ...newLead, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Last Name *</label>
                  <Input
                    placeholder="Last name"
                    value={newLead.lastName}
                    onChange={(e) => setNewLead({ ...newLead, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Phone *</label>
                <Input
                  placeholder="+91 98765 43210"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Source</label>
                  <Select value={newLead.source} onValueChange={(v) => setNewLead({ ...newLead, source: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Interest</label>
                  <Select value={newLead.interestType} onValueChange={(v) => setNewLead({ ...newLead, interestType: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {INTEREST_OPTIONS.map((i) => (
                        <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Budget</label>
                  <Input
                    placeholder="e.g., 50-75 Lakhs"
                    value={newLead.budget}
                    onChange={(e) => setNewLead({ ...newLead, budget: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Preferred Location</label>
                  <Input
                    placeholder="e.g., Nashik"
                    value={newLead.preferredLocation}
                    onChange={(e) => setNewLead({ ...newLead, preferredLocation: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Notes</label>
                <Textarea
                  placeholder="Additional notes..."
                  rows={3}
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={createLead} disabled={submitting} className="bg-emerald-900 hover:bg-emerald-800">
                  {submitting ? 'Creating...' : 'Create Lead'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-900">{stats.total || 0}</p>
            <p className="text-xs text-blue-700">Total Leads</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-emerald-900">{stats.today || 0}</p>
            <p className="text-xs text-emerald-700">Today</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-red-900">{stats.hot || 0}</p>
            <p className="text-xs text-red-700">Hot Leads 🔥</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-purple-900">{stats.qualified || 0}</p>
            <p className="text-xs text-purple-700">Qualified</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-900">{stats.converted || 0}</p>
            <p className="text-xs text-green-700">Converted</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-900">{stats.conversionRate || 0}%</p>
            <p className="text-xs text-amber-700">Conversion Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search by name, email, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearch}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.icon} {config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads List */}
      <Card>
        <CardHeader>
          <CardTitle>All Leads ({leads.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {leads.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No leads found</p>
              <Button 
                className="mt-4 bg-emerald-900 hover:bg-emerald-800"
                onClick={() => setCreateDialogOpen(true)}
              >
                Add your first lead
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {leads.map((lead) => {
                const statusConfig = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
                const priorityConfig = PRIORITY_CONFIG[lead.priority] || PRIORITY_CONFIG.warm;
                
                return (
                  <div 
                    key={lead.id} 
                    className="p-4 hover:bg-stone-50 cursor-pointer"
                    onClick={() => setSelectedLead(lead)}
                    data-testid={`lead-${lead.leadId}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center font-bold text-emerald-700">
                          {lead.firstName?.[0]}{lead.lastName?.[0]}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-stone-900">
                              {lead.firstName} {lead.lastName}
                            </span>
                            <span className="text-lg" title={priorityConfig.label}>
                              {priorityConfig.icon}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-stone-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {lead.phone}
                            </span>
                            {lead.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {lead.email}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-stone-400 font-mono">{lead.leadId}</span>
                            {lead.interestType && (
                              <span className="text-xs bg-stone-100 px-2 py-0.5 rounded capitalize">
                                {lead.interestType}
                              </span>
                            )}
                            {lead.budget && (
                              <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                                {lead.budget}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 ml-16 md:ml-0">
                        <div className="text-right">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
                            {statusConfig.label}
                          </span>
                          <p className="text-xs text-stone-400 mt-1">
                            Score: {lead.leadScore}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-stone-300" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lead Detail Dialog */}
      {selectedLead && (
        <LeadDetailDialog 
          lead={selectedLead} 
          onClose={() => setSelectedLead(null)}
          onUpdate={() => { fetchLeads(); fetchStats(); }}
        />
      )}
    </div>
  );
};

// Lead Detail Dialog Component
const LeadDetailDialog = ({ lead, onClose, onUpdate }) => {
  const [activities, setActivities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [newActivity, setNewActivity] = useState({ activityType: 'call', subject: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchLeadDetails();
  }, [lead.id]);

  const fetchLeadDetails = async () => {
    const token = localStorage.getItem('crm_token');
    try {
      const response = await axios.get(`${API}/leads/${lead.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivities(response.data.activities || []);
      setDeals(response.data.deals || []);
    } catch (error) {
    }
  };

  const addActivity = async () => {
    if (!newActivity.subject) {
      toast.error('Please enter activity subject');
      return;
    }

    const token = localStorage.getItem('crm_token');
    setSubmitting(true);

    try {
      await axios.post(`${API}/leads/${lead.id}/activities`, {
        ...newActivity,
        leadId: lead.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Activity added');
      setNewActivity({ activityType: 'call', subject: '', description: '' });
      fetchLeadDetails();
      onUpdate();
    } catch (error) {
      toast.error('Failed to add activity');
    } finally {
      setSubmitting(false);
    }
  };

  const statusConfig = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
  const priorityConfig = PRIORITY_CONFIG[lead.priority] || PRIORITY_CONFIG.warm;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-sm text-stone-400">{lead.leadId}</span>
            <span className="text-lg">{priorityConfig.icon}</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Lead Info */}
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-2xl font-bold text-emerald-700">
              {lead.firstName?.[0]}{lead.lastName?.[0]}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-stone-900">
                {lead.firstName} {lead.middleName || ''} {lead.lastName}
              </h2>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className={`px-3 py-1 text-xs rounded-full bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
                  {statusConfig.label}
                </span>
                <span className="px-3 py-1 text-xs rounded-full bg-stone-100 text-stone-700">
                  Score: {lead.leadScore}
                </span>
                <span className="px-3 py-1 text-xs rounded-full bg-stone-100 text-stone-700 capitalize">
                  {lead.source?.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-stone-400" />
              <span>{lead.phone}</span>
            </div>
            {lead.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-stone-400" />
                <span>{lead.email}</span>
              </div>
            )}
            {lead.preferredLocation && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-stone-400" />
                <span>{lead.preferredLocation}</span>
              </div>
            )}
            {lead.budget && (
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-stone-400" />
                <span>{lead.budget}</span>
              </div>
            )}
          </div>

          {/* Notes */}
          {lead.notes && (
            <div className="p-4 bg-stone-50 rounded-lg">
              <p className="text-sm text-stone-700">{lead.notes}</p>
            </div>
          )}

          {/* Add Activity */}
          <div className="border-t pt-4">
            <h3 className="font-medium text-stone-900 mb-3">Add Activity</h3>
            <div className="flex gap-3">
              <Select 
                value={newActivity.activityType} 
                onValueChange={(v) => setNewActivity({ ...newActivity, activityType: v })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="site_visit">Site Visit</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Activity subject"
                value={newActivity.subject}
                onChange={(e) => setNewActivity({ ...newActivity, subject: e.target.value })}
                className="flex-1"
              />
              <Button onClick={addActivity} disabled={submitting} className="bg-emerald-900 hover:bg-emerald-800">
                Add
              </Button>
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="border-t pt-4">
            <h3 className="font-medium text-stone-900 mb-3">Activity History</h3>
            {activities.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-4">No activities yet</p>
            ) : (
              <div className="space-y-3">
                {activities.map((activity) => (
                  <div key={activity.id} className="flex gap-3 p-3 bg-stone-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-stone-900">{activity.subject}</span>
                        <span className="text-xs bg-stone-200 px-2 py-0.5 rounded capitalize">
                          {activity.activityType.replace('_', ' ')}
                        </span>
                      </div>
                      {activity.description && (
                        <p className="text-sm text-stone-600 mt-1">{activity.description}</p>
                      )}
                      <p className="text-xs text-stone-400 mt-1">
                        {activity.performedByName} • {new Date(activity.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Deals */}
          {deals.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="font-medium text-stone-900 mb-3">Deals</h3>
              <div className="space-y-2">
                {deals.map((deal) => (
                  <div key={deal.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                    <div>
                      <p className="font-medium text-emerald-900">{deal.dealName}</p>
                      <p className="text-xs text-emerald-700">{deal.dealId} • {deal.stage}</p>
                    </div>
                    <p className="font-bold text-emerald-900">₹{deal.estimatedValue?.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeadsPage;
